import { Col, Row } from "antd";
import dayjs from "dayjs";
import { get, set } from "lodash";
import { useTranslation } from "react-i18next";
import { MdOutlineFrontHand } from "react-icons/md";
import Alert from "shared/components/Alert";
import Form from "shared/components/Form";
import FormField from "shared/components/FormField";
import Input from "shared/components/Input";
import Select from "shared/components/Select";
import { NAME_SPACES } from "shared/locales/constants";
import {
  TERMINATION_MOTIFS,
  TERMINATION_RECIPIENTS,
} from "utils/api/graphql/queries/termination";
import { DATE_FORMAT, SUBSCRIPTION_FORM_KEYS } from "utils/constants";
import { filterContractNumber } from "utils/helpers/validations";

const TerminationRequest = ({
  initialValues,
  setPercent,
  bindField,
  isFieldRequired,
  setDisabled,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  const selfOccupationPath = "project.fields.selfOccupation";
  const selfOccupationLabel =
    "L'adhérent souhaite que La Mutuelle Familiale s'occupe de la résiliation.";
  const selfOccupationField = bindField(selfOccupationPath, {
    label: selfOccupationLabel,
    extractValue: (checked) => !checked,
    onChange: (value) => {
      setDisabled((prev) => {
        const next = {
          ...prev,
          project: {
            ...prev?.project,
            terminationRequest: {
              ...prev?.project?.terminationRequest,
              contractNumber: !value,
              dueDate: !value,
              recipient: !value,
              reason: !value,
            },
          },
        };
        setPercent(initialValues, next);
        return next;
      });
    },
    onBlur: () => setPercent(initialValues),
  });

  const contractNumberPath = "project.terminationRequest.contractNumber";
  const contractNumberField = bindField(contractNumberPath, {
    label: SUBSCRIPTION.FORM.CONTRACT_NUMBER.LABEL,
    required: isFieldRequired(contractNumberPath),
    extractValue: ({ target: { value } }) => filterContractNumber(value),
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const dueDatePath = "project.terminationRequest.dueDate";
  const dueDateField = bindField(dueDatePath, {
    label: SUBSCRIPTION.FORM.CURRENT_EFFECTIVE_DATE.LABEL,
    required: isFieldRequired(dueDatePath),
    onChange: (date) => {
      const isValid = date && dayjs(date).isBefore(dayjs());
      if (!isValid) {
        set(initialValues, dueDatePath, null);
      }
      setPercent(initialValues);
    },
    onBlur: () => setPercent(initialValues),
  });

  const recipientPath = "project.terminationRequest.recipient";
  const recipientField = bindField(recipientPath, {
    label: SUBSCRIPTION.FORM.CURRENT_COMPANY.LABEL,
    required: isFieldRequired(recipientPath),
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const reasonPath = "project.terminationRequest.reason";
  const reasonField = bindField(reasonPath, {
    label: SUBSCRIPTION.FORM.TERMINATION_MOTIFS.LABEL,
    required: isFieldRequired(reasonPath),
    onChange: () => setPercent(initialValues),
    onBlur: () => setPercent(initialValues),
  });

  const showTerminationFields =
    get(initialValues, "project.ria") &&
    !get(initialValues, selfOccupationPath);

  return (
    get(initialValues, "project.ria", false) && (
      <div
        className="subscription-form--wrapper"
        id={`${SUBSCRIPTION_FORM_KEYS.ADDITIONAL_INFORMATION.TERMINATION_REQUEST}-section`}
      >
        <Form
          type="vertical"
          title={
            SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.TERMINATION_REQUEST.TITLE
          }
          subtitle={
            SUBSCRIPTION.FORM.ADDITIONAL_INFORMATION.TERMINATION_REQUEST
              .SUBTITLE
          }
        >
          <Row gutter={[16, 16]}>
            <Col lg={24} xs={24}>
              <Alert
                showIcon
                type="info"
                message={SUBSCRIPTION.FORM.TERMINATION_ALERT}
              />
            </Col>
            <Col lg={24} xs={24}>
              <FormField
                path={selfOccupationField.path}
                label={selfOccupationField.label}
                required={selfOccupationField.required}
                disabled={selfOccupationField.disabled}
              >
                {(field) => (
                  <Input
                    type="switch"
                    label={selfOccupationLabel}
                    defaultChecked={
                      get(initialValues, selfOccupationPath, true) === false
                    }
                    {...field}
                    onChange={selfOccupationField.onChange}
                    onBlur={selfOccupationField.onBlur}
                  />
                )}
              </FormField>
            </Col>
            {showTerminationFields && (
              <>
                <Col lg={24} xs={24}>
                  <FormField
                    path={contractNumberField.path}
                    label={contractNumberField.label}
                    required={contractNumberField.required}
                    disabled={contractNumberField.disabled}
                  >
                    {(field) => (
                      <Input
                        placeholder={
                          SUBSCRIPTION.FORM.CONTRACT_NUMBER.PLACEHOLDER
                        }
                        label={contractNumberField.label}
                        icon={<MdOutlineFrontHand size={16} />}
                        type="custom"
                        stripSpaces={true}
                        defaultValue={contractNumberField.defaultValue}
                        {...field}
                        onChange={contractNumberField.onChange}
                        onBlur={contractNumberField.onBlur}
                        optional={!contractNumberField.required}
                      />
                    )}
                  </FormField>
                </Col>
                <Col xs={24} sm={24} md={24} lg={24}>
                  <FormField
                    path={dueDateField.path}
                    label={dueDateField.label}
                    required={dueDateField.required}
                    disabled={dueDateField.disabled}
                  >
                    {(field) => (
                      <Input
                        label={dueDateField.label}
                        placeholder={
                          SUBSCRIPTION.FORM.CURRENT_EFFECTIVE_DATE.PLACEHOLDER
                        }
                        type="date"
                        variant="simple"
                        format={DATE_FORMAT}
                        disabledDate={(current) => current && current > dayjs()}
                        defaultValue={
                          get(initialValues, dueDatePath)
                            ? dayjs(get(initialValues, dueDatePath))
                            : undefined
                        }
                        {...field}
                        onChange={dueDateField.onChange}
                        onBlur={dueDateField.onBlur}
                        optional={!dueDateField.required}
                      />
                    )}
                  </FormField>
                </Col>
                <Col xs={24} sm={24} md={24} lg={24}>
                  <FormField
                    path={recipientField.path}
                    label={recipientField.label}
                    required={recipientField.required}
                    disabled={recipientField.disabled}
                  >
                    {(field) => (
                      <Select
                        type="async"
                        node={{
                          query: TERMINATION_RECIPIENTS,
                          variables: { where: { id: "54" } },
                          accessors: {
                            root: "terminationRecipients.data",
                            label: "marque",
                            value: "id",
                          },
                        }}
                        label={recipientField.label}
                        placeholder={
                          SUBSCRIPTION.FORM.CURRENT_COMPANY.PLACEHOLDER
                        }
                        defaultValue={recipientField.defaultValue}
                        {...field}
                        onChange={recipientField.onChange}
                        onBlur={recipientField.onBlur}
                        optional={!recipientField.required}
                      />
                    )}
                  </FormField>
                </Col>
                <Col xs={24} sm={24} md={24} lg={24}>
                  <FormField
                    path={reasonField.path}
                    label={reasonField.label}
                    required={reasonField.required}
                    disabled={reasonField.disabled}
                  >
                    {(field) => (
                      <Select
                        type="async"
                        node={{
                          query: TERMINATION_MOTIFS,
                          variables: {},
                          accessors: {
                            root: "terminationMotifs.data",
                            label: "nom",
                            value: "id",
                          },
                        }}
                        label={reasonField.label}
                        placeholder={
                          SUBSCRIPTION.FORM.TERMINATION_MOTIFS.PLACEHOLDER
                        }
                        value={get(initialValues, reasonPath)}
                        defaultValue={reasonField.defaultValue}
                        {...field}
                        onChange={reasonField.onChange}
                        onBlur={reasonField.onBlur}
                        optional={!reasonField.required}
                      />
                    )}
                  </FormField>
                </Col>
              </>
            )}
          </Row>
        </Form>
      </div>
    )
  );
};

export default TerminationRequest;
